import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import userUtils from 'utils/User';
import authenticationUtils from 'utils/Authentication';
import { Box, Flex } from 'components/ui';
import Actions from 'Actions';
import Chevron from 'components/Chevron/Chevron';
import UrlUtils from 'utils/Url';
import {
    borders, colors, space, radii, mediaQueries
} from 'style/config';
import RegisterStoreMember from 'components/Register/RegisterStoreMember';
import RegisterEmailLookup from 'components/Register/RegisterEmailLookup';
import RegisterForm from 'components/Register/RegisterForm';
import { extractOAuth2Params, checkIsOAuth2Flow } from 'utils/authGrant';

import RegisterEN from 'components/Register/locales/Register_en_US';
import RegisterFR from 'components/Register/locales/Register_fr_CA';
import localeUtils from 'utils/LanguageLocale';

const getText = localeUtils.getLocalisedDataFromResource(RegisterEN, RegisterFR);
const AUTH_CONTENT_WIDTH = 400;

/**
 * Register Page
 * Full-screen Register page that handles both regular Register and OAuth2 external authentication
 */
const Register = () => {
    const dispatch = useDispatch();

    const [lookup, setLookup] = useState(true);
    const [email, setEmail] = useState('');
    const [isInStoreMember, setIsInStoreMember] = useState(false);
    const [isOAuth2Flow, setIsOAuth2Flow] = useState(false);

    useEffect(() => {
        // Check if RefreshToken service has flagged for signout on init
        if (window.Sephora?.Util?.RefreshToken?.isSignOutOnInit) {
            UrlUtils.redirectTo('/');
        }

        const params = extractOAuth2Params();
        setIsOAuth2Flow(checkIsOAuth2Flow(params));

        const handleLoggedInUser = async () => {
            await userUtils.validateUserStatus();

            const isSignedIn = userUtils.isSignedIn();

            if (isSignedIn) {
                UrlUtils.redirectTo('/');
            }
        };

        handleLoggedInUser();
    }, []);

    const handleEmailChange = (e, loginHint = '') => setEmail(e?.target?.value || loginHint);
    const handleStoreMember = flag => {
        setIsInStoreMember(flag);
    };

    const handleLookup = flag => {
        setLookup(flag);
    };

    const handleSignIn = () => {
        dispatch(Actions.showInterstice(false));

        if (authenticationUtils.isNewSignInFlowEnabled()) {
            // ACS-4437 (RUN-03, AC-4/AC-6): gate ON routes through the new /signin
            // flow. No credential is carried — the user has not entered one here.
            authenticationUtils.navigateToSignIn();
        } else {
            // Legacy path (unchanged) — gate OFF.
            const urlParams = new URLSearchParams(window.location.search);
            const params = urlParams.toString();
            UrlUtils.redirectTo(`/login${params ? `?${params}` : ''}`);
        }
    };

    const handleClose = () => {
        const isOnSecondPage = !lookup || isInStoreMember;

        if (isOnSecondPage) {
            setLookup(true);
            setIsInStoreMember(false);
        } else if (window.history.length > 1) {
            dispatch(Actions.showInterstice(false));
            window.history.back();
        } else {
            handleSignIn();
        }
    };

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='flex-start'
            >
                {/* Header with Back Button and Logo */}
                <Flex css={styles.headerContainer}>
                    <Box
                        is='button'
                        type='button'
                        aria-label={getText('backButtonAriaLabel')}
                        css={styles.backButton}
                        onClick={handleClose}
                    >
                        <Chevron
                            direction={'left'}
                            color={colors.black}
                            css={styles.chevron}
                        />
                    </Box>

                    {/* Logo */}
                    <Box css={styles.logoContainer}>
                        <img
                            src='/img/ufe/logo.svg'
                            alt='Sephora'
                            style={styles.logo}
                        />
                    </Box>
                </Flex>
                <Box paddingX={4}>
                    <Box css={styles.contentWrapper}>
                        {isInStoreMember ? (
                            <RegisterStoreMember
                                email={email}
                                handleSignIn={handleSignIn}
                                isOAuth2Flow={isOAuth2Flow}
                            />
                        ) : lookup ? (
                            <RegisterEmailLookup
                                handleLookup={handleLookup}
                                handleSignIn={handleSignIn}
                                handleEmailChange={handleEmailChange}
                                handleStoreMember={handleStoreMember}
                                isOAuth2Flow={isOAuth2Flow}
                            />
                        ) : (
                            <RegisterForm
                                email={email}
                                handleSignIn={handleSignIn}
                                handleEmailChange={handleEmailChange}
                            />
                        )}
                    </Box>
                </Box>
                {/* End Form Container */}
            </Box>
        </div>
    );
};

const styles = {
    headerContainer: {
        position: 'relative',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: `${borders[1]} ${colors.lightGray}`
    },
    backButton: {
        position: 'absolute',
        top: space[5],
        left: space[5],
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: space[2],
        margin: -space[2],
        borderRadius: radii[2],
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: colors.lightGray
        },
        ':focus:not(:focus-visible)': {
            outline: 'none'
        },
        ':focus-visible': {
            outline: `2px solid ${colors.black}`,
            outlineOffset: 2
        }
    },
    chevron: {
        width: space[4],
        height: space[4]
    },
    logoContainer: {
        marginBottom: space[5],
        marginTop: space[5]
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    contentWrapper: {
        marginTop: space[7] + space[3],
        marginBottom: space[7] + space[3],
        width: AUTH_CONTENT_WIDTH,
        [mediaQueries.smMax]: {
            marginTop: space[5],
            marginBottom: space[5],
            width: '100%'
        }
    }
};

export default wrapFunctionalComponent(Register, 'Register');
